import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  ApiError,
  updateZonePricing,
  type PartySizeTierInput,
  type PricingUnit,
  type Zone,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly zone: Zone;
  readonly onSaved: () => void;
}

type TierRow = PartySizeTierInput;

const UNIT_OPTIONS: { value: PricingUnit; label: string }[] = [
  { value: 'per_hour_per_person', label: '每小時 × 每人' },
  { value: 'per_hour_per_seat', label: '每小時 × 每格位' },
  { value: 'flat_per_hour', label: '整場固定（每小時）' },
  { value: 'tiered_per_person', label: '階梯式（依人數不同單價）' },
];

export function ZonePricingModal({ opened, onClose, zone, onSaved }: Props): JSX.Element {
  const [enabled, setEnabled] = useState(zone.pricing !== null);
  const [amount, setAmount] = useState<number | ''>(zone.pricing?.amount ?? '');
  const [unit, setUnit] = useState<PricingUnit>(
    (zone.pricing?.unit as PricingUnit | undefined) ?? 'per_hour_per_person',
  );
  const [tiers, setTiers] = useState<TierRow[]>(() => normalizeTiers(zone.pricing?.tiers));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setEnabled(zone.pricing !== null);
    setAmount(zone.pricing?.amount ?? '');
    setUnit((zone.pricing?.unit as PricingUnit | undefined) ?? 'per_hour_per_person');
    setTiers(normalizeTiers(zone.pricing?.tiers));
    setErr(null);
  }, [opened, zone]);

  const validationError = useMemo(() => {
    if (!enabled) return null;
    if (unit !== 'tiered_per_person') {
      if (typeof amount !== 'number' || amount <= 0) return '金額必須是大於 0 的數字';
      return null;
    }
    // 階梯驗證：每筆 min 遞增、max = 前一筆 min − 1 +1 或 null 尾、金額 > 0
    if (tiers.length === 0) return '至少需要 1 個階梯';
    let prevMax: number | null = null;
    for (let i = 0; i < tiers.length; i += 1) {
      const t = tiers[i]!;
      if (t.minPartySize < 1) return `第 ${i + 1} 段：最小人數需 ≥ 1`;
      if (t.maxPartySize !== null && t.maxPartySize < t.minPartySize) {
        return `第 ${i + 1} 段：最大人數需 ≥ 最小人數`;
      }
      if (t.maxPartySize === null && i !== tiers.length - 1) {
        return `第 ${i + 1} 段：只有最後一段可以「無上限」`;
      }
      if (t.amountPerPersonPerHour <= 0) return `第 ${i + 1} 段：每人金額需 > 0`;
      if (i > 0 && prevMax !== null && t.minPartySize !== prevMax + 1) {
        return `第 ${i + 1} 段：最小人數必須銜接前一段（無間隙 / 無重疊）`;
      }
      prevMax = t.maxPartySize;
    }
    return null;
  }, [enabled, unit, amount, tiers]);

  async function handleSave(): Promise<void> {
    setErr(null);
    if (!enabled) {
      setSaving(true);
      try {
        await updateZonePricing(zone.resourceId, null);
        notifications.show({
          color: 'green',
          title: '價格已更新',
          message: `${zone.zoneLabel} 已改為免費`,
        });
        onSaved();
        onClose();
      } catch (e) {
        setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (validationError) {
      setErr(validationError);
      return;
    }

    setSaving(true);
    try {
      if (unit === 'tiered_per_person') {
        const firstTierAmount = tiers[0]?.amountPerPersonPerHour ?? 0;
        await updateZonePricing(zone.resourceId, {
          amount: firstTierAmount,
          unit,
          currency: 'TWD',
          tiers,
        });
      } else {
        if (typeof amount !== 'number') return;
        await updateZonePricing(zone.resourceId, {
          amount,
          unit,
          currency: 'TWD',
          tiers: null,
        });
      }
      notifications.show({
        color: 'green',
        title: '價格已更新',
        message: `${zone.zoneLabel} 定價已更新`,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setSaving(false);
    }
  }

  function addTier(): void {
    const prev = tiers[tiers.length - 1];
    const newMin = prev ? (prev.maxPartySize ?? prev.minPartySize) + 1 : 2;
    // 舊的最後一段若是無上限 → 先把它改成有上限（以便新段接著）
    const updated = prev && prev.maxPartySize === null
      ? tiers.slice(0, -1).concat({ ...prev, maxPartySize: prev.minPartySize })
      : tiers.slice();
    updated.push({ minPartySize: newMin, maxPartySize: null, amountPerPersonPerHour: 500 });
    setTiers(updated);
  }

  function removeTier(i: number): void {
    const next = tiers.filter((_, idx) => idx !== i);
    if (next.length > 0) {
      // 最後一段永遠強制 maxPartySize = null（開放上限）
      const last = next[next.length - 1]!;
      next[next.length - 1] = { ...last, maxPartySize: null };
    }
    setTiers(next);
  }

  function updateTier(i: number, patch: Partial<TierRow>): void {
    setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`編輯價格 · ${zone.zoneLabel}`} centered size="lg">
      <Stack gap="sm">
        <Switch
          label="收費"
          description="關閉代表免費（例如試營運 / 內部使用）"
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
          color={tokens.color.mantine['primary']}
        />
        {enabled && (
          <>
            <Select
              label="計費單位"
              data={UNIT_OPTIONS}
              value={unit}
              onChange={(v) => v && setUnit(v as PricingUnit)}
              allowDeselect={false}
            />

            {unit === 'tiered_per_person' ? (
              <TierEditor tiers={tiers} onAdd={addTier} onRemove={removeTier} onUpdate={updateTier} />
            ) : (
              <>
                <NumberInput
                  label={unit === 'flat_per_hour' ? '整場時薪（TWD / 小時）' : '金額（TWD）'}
                  placeholder="例：250"
                  min={1}
                  step={10}
                  value={amount}
                  onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
                  required
                />
                <Text size="xs" c="dimmed">
                  計算範例：{previewLabel(amount, unit)}
                </Text>
              </>
            )}
          </>
        )}
        {(err || validationError) && (
          <Alert color="red" variant="light">
            {err ?? validationError}
          </Alert>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            color={tokens.color.mantine['primary']}
            onClick={() => void handleSave()}
            loading={saving}
          >
            儲存
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TierEditor({
  tiers,
  onAdd,
  onRemove,
  onUpdate,
}: {
  readonly tiers: TierRow[];
  readonly onAdd: () => void;
  readonly onRemove: (i: number) => void;
  readonly onUpdate: (i: number, patch: Partial<TierRow>) => void;
}) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          階梯級距
        </Text>
        <Text size="xs" c="dimmed">
          最後一段必須是「無上限」（最大人數留空）。各段需連續、無間隙與重疊。
        </Text>
        <Divider />
        <Table withRowBorders={false} verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 90 }}>最小人數</Table.Th>
              <Table.Th style={{ width: 110 }}>最大人數</Table.Th>
              <Table.Th>每人金額（TWD）</Table.Th>
              <Table.Th style={{ width: 40 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tiers.map((t, i) => {
              const isLast = i === tiers.length - 1;
              return (
                <Table.Tr key={i}>
                  <Table.Td>
                    <NumberInput
                      value={t.minPartySize}
                      onChange={(v) =>
                        onUpdate(i, { minPartySize: typeof v === 'number' ? v : t.minPartySize })
                      }
                      min={1}
                      step={1}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      placeholder={isLast ? '無上限' : ''}
                      value={t.maxPartySize ?? ''}
                      onChange={(v) => {
                        if (v === '' || v === undefined) {
                          onUpdate(i, { maxPartySize: isLast ? null : t.maxPartySize });
                        } else if (typeof v === 'number') {
                          onUpdate(i, { maxPartySize: v });
                        }
                      }}
                      min={t.minPartySize}
                      step={1}
                      size="xs"
                      disabled={isLast}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={t.amountPerPersonPerHour}
                      onChange={(v) =>
                        onUpdate(i, {
                          amountPerPersonPerHour: typeof v === 'number' ? v : t.amountPerPersonPerHour,
                        })
                      }
                      min={1}
                      step={50}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => onRemove(i)}
                      aria-label="刪除此階梯"
                      disabled={tiers.length <= 1}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        <Group justify="flex-end">
          <Button
            leftSection={<IconPlus size={14} />}
            variant="light"
            size="xs"
            onClick={onAdd}
          >
            新增一段
          </Button>
        </Group>
        <Divider />
        <Text size="xs" c="dimmed">
          試算（1 小時）：{' '}
          {tiers
            .map(
              (t) =>
                `${t.minPartySize}${t.maxPartySize ? `–${t.maxPartySize}` : '+'}人 → 每人 $${t.amountPerPersonPerHour}`,
            )
            .join('；')}
        </Text>
      </Stack>
    </Paper>
  );
}

function normalizeTiers(raw: readonly PartySizeTierInput[] | null | undefined): TierRow[] {
  if (!raw || raw.length === 0) {
    // 預設 2 段樣板，staff 可刪減／微調
    return [
      { minPartySize: 2, maxPartySize: 2, amountPerPersonPerHour: 1200 },
      { minPartySize: 3, maxPartySize: null, amountPerPersonPerHour: 800 },
    ];
  }
  return raw.map((t) => ({ ...t }));
}

function previewLabel(amount: number | '', unit: PricingUnit): string {
  if (typeof amount !== 'number') return '—';
  switch (unit) {
    case 'per_hour_per_person':
      return `4 人 × 2 小時 = NT$${amount * 4 * 2}`;
    case 'per_hour_per_seat':
      return `3 格 × 2 小時 = NT$${amount * 3 * 2}`;
    case 'flat_per_hour':
      return `1.5 小時整場 = NT$${Math.round(amount * 1.5)}`;
    default:
      return '—';
  }
}
